import { Api } from "./index";
import * as Text from "../i18n/en-gb.json";
import type { State } from "./state";
import type { Moment } from "moment";
import type { MutationTree } from "vuex";

export default {
    setCalendarHasDoublePaneState: (state: State, bool: boolean) => { state.calendarHasDoublePane = bool },
    setDemoState: (state: State, demo: boolean) => { state.demoMode = demo },
    setErrorMessageVisible: (state: State, visable: boolean) => { state.errorMessageVisible = visable },

    toggleGuestWindow: (state: State) => {
        if (state.selectedDate.length == 0) {
            state.errorMessageVisible = true;
            state.errorMessageText = 'Guests have changed, please re-select your dates.';
            state.errorMessageClass = 'ac-checker-error  ac-checker-notice';
        }
        state.showGuestWindow = !state.showGuestWindow;
    },

    hideGuestWindow: (state: State) => { state.showGuestWindow = false },
    toggleCalendarWindow: (state: State) => { state.showCalendar = !state.showCalendar },
    hideCalendarWindow: (state: State) => { state.showCalendar = false },

    hideKeyOnRequest: (state: State) => {
        state.showKeyCheckInOut = true;
        state.showKeyOnRequest = false;
    },

    hideKeyCheckInOut: (state: State) => {
        state.showKeyCheckInOut = false;
        state.showKeyOnRequest = true;
    },

    updateGuestsTotals: (state: State, info: number) => {
        state.requirements.totalGuests = info
    },

    updateGuestInput: (state: State, text: string) => {
        state.guestSelector.guestInputValue = text;
    },

    updateAllowRules: (state: State, rules: State["guestSelector"]["selector"]) => {
        // Adults are always allowed so there is no need to set allow or max
        state.guestSelector.selector.adults.ipm_value = rules.adults.ipm_value;

        state.guestSelector.selector.children.ipm_value = rules.children.ipm_value;
        state.guestSelector.selector.children.allowed = rules.children.allowed;
        state.guestSelector.selector.children.max = rules.children.max;
        if ( rules.children.allowed == false ) {
            state.guestSelector.selector.children.label = 'No children allowed';
        }

        state.guestSelector.selector.babies.ipm_value = rules.babies.ipm_value;
        state.guestSelector.selector.babies.allowed = rules.babies.allowed;
        state.guestSelector.selector.babies.max = rules.babies.max;

        if ( rules.babies.allowed == false ) {
            state.guestSelector.selector.babies.label = 'No babies allowed';
        }

        state.guestSelector.selector.pets.ipm_value = rules.pets.ipm_value;
        state.guestSelector.selector.pets.allowed = rules.pets.allowed;
        state.guestSelector.selector.pets.max = rules.pets.max;

        if ( rules.pets.allowed == false ) {
            state.guestSelector.selector.pets.label = 'No pets allowed';
        }
    },

    clearSelectedDates: (state: State) => {
        state.selectedDate = [];
        state.calendarTitle = 'Select your check-in';
        state.totalNightsSelected = '';
        state.dateSelectionComplete = false;
    },

    updateClearState: (state: State, clearStateValue: boolean) =>  { state.clearState = clearStateValue },
    updateDefaultState: (state: State, defaultState: boolean) => { state.defaultState = defaultState },
    updateForceReload: (state: State, forceStatus: boolean) => { state.forceReload = forceStatus },

    updateSelectedDates: (state: State, date: Date|string) => {
        (date === 'none') ? state.selectedDate = [] : state.selectedDate.push(date);
    },

    datesApplied: (state: State, bool: boolean) => { state.dateSelectionComplete = bool },
    setPickerAttributes: (state: State, attributes: Array<any>) => { state.pickerAttributes = attributes },
    setBookablesAttributes: (state: State, attributes: Array<any>) => { state.bookableAttributes = attributes },

    removeStartAndEndDates: (state: State) => {
        state.selectedDate.splice(0,2);
    },

    lockGuestInput: (state: State, lock: boolean) => {
        if ( lock ) {
            state.guestSelector.selector.adults.max = state.guestSelector.selector.adults.ipm_value;
            if ( state.guestSelector.selector.children.allowed ) {
                state.guestSelector.selector.children.max = state.guestSelector.selector.children.ipm_value;
            }
        } else {
            state.guestSelector.selector.adults.max = 100;
            if ( state.guestSelector.selector.children.allowed ) {
                state.guestSelector.selector.children.max = 100;
            }
        }
    },

    setMaxEndDate: (state: State, maxEndDate: string) => {
        state.maxEndDate = maxEndDate;
    },

    setMinMaxDates: (state: State, dates: State["apiDates"]["minMaxDates"]) => {
        state.apiDates.minMaxDates = dates;
    },

    updateStartDateRules: (state: State, dates: { min: Moment, max: Moment }) => {
        state.apiDates.startDateRules = dates;
        state.apiDates.minMaxDates.end = dates.max;
    },
    addUnavailableDates: (state: State, dates: any) => {
        state.apiDates.selectedDatesUnavailable.push(dates);
    },
    storeTempUnavailableDates: (state: State, dates: any) => {
        state.apiDates.tempUnavailableDatesStore.push(dates);
    },
    updateCalendarTitle: (state: State, title: string) => {
        state.calendarTitle = title;
    },
    setCalendarText: (state: State, text: string) => {
        state.calendarText = text;
    },

    /**
     * Update the text on the calendar "Check In" label
     *
     * @param state Application state
     * @param text  Check in date
     */
    setCalendarCheckInText: (state: State, text: string) => {
        state.calendarTextCheckIn = text;
    },

    /**
     * Update the text on the calendar "Check out" label
     *
     * @param state Application state
     * @param text  Check out date
     */
    setCalendarCheckOutText: (state: State, text: string) => {
        state.calendarTextCheckOut = text;
    },

    /**
     * Reset both the "Check in" and "Check out" label
     *
     * @param state Application state
     */
    resetCalendarCheckInOutText: (state: State) => {
        state.calendarTextCheckIn = Text.calendar.checkIn;
        state.calendarTextCheckOut = Text.calendar.checkOut;
    },
    setKeyCheckInOutText: (state: State, text: string) => {
        state.keyCheckInOutText = text;
    },
    loading: (state: State, status: boolean) => {
        state.isLoading = status;
    },
    setPlaceInfo: (state: State, current_place: State["place"]) => {
        Api.base_url = current_place.api_endpoint;
        state.place = current_place;
        state.guestSelector.selector.children.allowed = state.place.suitability.children > 0;
        state.guestSelector.selector.pets.allowed = state.place.suitability.pets > 0;
        state.showBookablesSection = state.place.show_accommodation;
    },
    setAvailabilityData: (state: State, response: State["apiData"]) => {
        state.apiData = {...state.apiData, ...response};
    },
    setNightsTotal: (state: State, nights: number) => {
        state.requirements.totalNights = nights;
    },
    updateBookables: (state: State, bookableNames: Array<any>) => {
        state.bookables = bookableNames;
    },
    updateBookablesAvailability: (state: State, bookablesAvailability: Array<any>) => {
        state.apiDates.bookables_data = bookablesAvailability;
    },
    updateUnavailableDates: (state: State, dates: State["apiDates"]["place"]) => {
        state.apiDates.place = dates;
    },
    setRequiredCapacity: (state: State, capacity: State["requirements"]["capacity"]) => {
        state.requirements.capacity = {
            daily: capacity.daily,
            total: capacity.total
        };
    },
    setNightsText: (state: State, text: string) => {
        state.totalNightsSelected = text;
    },
    setAPIDates: (state: State, allAPIDates: State["apiDates"]["place"]) => {
        state.apiDates.place.availableCheckInDates = state.apiDates.place.availableCheckInDates.concat( allAPIDates.availableCheckInDates );
        state.apiDates.place.availableCheckOutDates = state.apiDates.place.availableCheckOutDates.concat( allAPIDates.availableCheckOutDates );
        state.apiDates.place.disabledDates = state.apiDates.place.disabledDates.concat( allAPIDates.disabledDates );
    },
    updateBookablesCalendarDates: (state: State, addBookableCalendarData: State["bookableCalendar"]) => {
        state.bookableCalendar.availableCheckInDates = state.bookableCalendar.availableCheckInDates.concat(addBookableCalendarData.availableCheckInDates);
        state.bookableCalendar.availableCheckOutDates = state.bookableCalendar.availableCheckOutDates.concat(addBookableCalendarData.availableCheckOutDates);
        state.bookableCalendar.disabledDates = state.bookableCalendar.disabledDates.concat(addBookableCalendarData.disabledDates);
    },
    setBookingCalendarToShow: (state: State, bookable: number|null) => {
        state.showBookableCalendar = bookable;
    }
} as MutationTree<State>;
